import React from 'react'
import { useTranslation } from 'next-i18next'
import { FormControl } from '@mui/material'
import { isValidUrl } from 'utils/formatUtils'
import VisualExamples from './VisualExamples'
import { Textarea } from '@components/ui/textarea'
import { Button } from '@components/ui/button'
import { X } from 'lucide-react'
import { Input } from '@components/ui/input'

const TextType = ({
  itemKey,
  type,
  name,
  value,
  negativePromptExamples,
  selectedArgsExample,
  updateArgValues,
  onChange,
}) => {
  const { t } = useTranslation('common')

  // Check if this is a prompt field (for special styling)
  const isPrompt =
    itemKey === 'prompt' ||
    name?.toLowerCase() === 'prompt' ||
    itemKey === 'negative_prompt' ||
    name?.toLowerCase() === 'negative prompt' ||
    itemKey?.toLowerCase().includes('prompt') ||
    name?.toLowerCase()?.includes('prompt')

  const isSeed = itemKey === 'seed' || name?.toLowerCase() === 'seed'

  return (
    <FormControl fullWidth className="input-container">
      {isPrompt ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            {value && (
              <Button variant="ghost" size="icon" className="h-6 w-6 ml-auto" onClick={() => onChange('')}>
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          <Textarea
            placeholder={`Enter ${name ?? 'prompt'}`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="min-h-[100px] resize-none"
          />
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">{value?.length || 0}/500 characters</span>
          </div>
        </div>
      ) : isSeed ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
              placeholder="Enter seed"
              defaultValue={value || Math.floor(Math.random() * 90000) + 10000}
              className="bg-white w-full text-sm"
            />
            <Button
              variant="outline"
              size="sm"
              className="ml-2 whitespace-nowrap text-xs h-9"
              onClick={() => onChange(Math.floor(Math.random() * 90000) + 10000)}
            >
              Random
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Use the same seed to get consistent results with the same prompt
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {type === 'url' ? (
            <Input
              placeholder={`Enter ${name ?? 'URL'}`}
              onChange={(e) => onChange(e.target.value)}
              value={value || ''}
              className="w-full"
            />
          ) : (
            <Textarea
              placeholder={`Enter ${name ?? ''}`}
              onChange={(e) => onChange(e.target.value)}
              value={value || ''}
              className="min-h-[80px] resize-none"
            />
          )}
          {type === 'url' && value && !isValidUrl(value) && (
            <p className="text-xs text-red-500">URL is invalid. Please add a valid URL!</p>
          )}
        </div>
      )}

      <VisualExamples
        updateArgValues={updateArgValues}
        itemKey={itemKey}
        options={selectedArgsExample}
        selectedOption={value}
        labelKey={'arg_value'}
        imageKey={'preview_media'}
      />

      {negativePromptExamples?.arg_value && (
        <div>
          <div className="font-medium text-center leading-5 text-[#101828] flex items-center mt-4">
            <span className="h-[1px] md:w-1/3 w-1/5 bg-[#909090]"></span>
            <span className="text-[10px] md:text-sm md:w-1/3 w-3/5 ">{t('model.try-example')}</span>
            <span className="h-[1px] md:w-1/3 w-1/5 bg-[#909090]"></span>
          </div>
          <div className="custom-scrollbar-horizontal mt-4 md:mt-4 gap-2 pb-3 flex overflow-x-auto overflow-y-hidden bg-[#fff]">
            {negativePromptExamples.arg_value.map((items, i) => {
              return (
                <div
                  className="w-auto flex-shrink-0 cursor-pointer overflow-hidden"
                  key={`sm-imgs-${i}`}
                  onClick={() => {
                    let updatedValue
                    if (value.includes(items.arg_value))
                      updatedValue = value.replace(items.arg_value, '').replace(', ,', ',')
                    else updatedValue = `${value}${value.trim().length ? ', ' : ''}${items.arg_value}`

                    if (updatedValue.slice(-2) == ', ') updatedValue = updatedValue.slice(0, -2)
                    if (updatedValue[0] === ',') updatedValue = updatedValue.slice(1)
                    updateArgValues(itemKey, updatedValue?.trim())
                  }}
                >
                  {items?.preview_media && <img src={items.preview_media} alt="ex" className="rounded" />}
                  <p
                    className={`rounded-full py-2 px-3 text-xs font-medium text-center mt-1 overflow-hidden text-ellipsis bg-[#f2f2f2] ${value.includes(items.arg_value) && 'bg-black text-white'}`}
                  >
                    {items.arg_value}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </FormControl>
  )
}

export default TextType
